import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { CardActionArea } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import getCharacteristicsImagePath from "../../services/image/imageService";
import CharacteristicsEditor from "../characteristics/CharacteristicsEditor";
import { createMatchEvent, getCurrentLocation } from "../../services/api/eventService";
import { useHistoryDrawer } from "../../contexts/historyDrawerContext";
import { useSearch } from "../../contexts/searchContext";
import { CognitoUserContext } from "../../contexts/cognitoUserContext";
import { canEditPerson } from "../../services/permissions";
import { useSettings } from "../../contexts/settingsContext";
import { getTenantId, getPersonImageUrl } from "../../config/tenantConfig";

function PersonCard({ person, active, matchSource = 'manual_filter' }) {
  const { cognitoUser } = React.useContext(CognitoUserContext);
  const tenantId = getTenantId(cognitoUser);

  const {
    openHistoryDrawer,
    openAssignments,
    updateCachedLocation,
    getLocationPromise,
    setLocationPromise,
    addPendingMatchEvent
  } = useHistoryDrawer();
  const { matchAccuracy, capturedImageS3Key } = useSearch();
  const settings = useSettings();
  const canEdit = canEditPerson(cognitoUser);
  const [editorOpen, setEditorOpen] = React.useState(false);

  const handleMatchClick = async (e) => {
    e.stopPropagation();

    const personName = `${person.first_name || ''} ${person.last_name || ''}`.trim() || 'Unknown';
    const capturedImage = matchSource === 'face_match' ? capturedImageS3Key : null;

    // Debug: Log captured image info
    if (matchSource === 'face_match') {
    }

    // Get open assignment from cached context (no API call!)
    const openAssignment = openAssignments.length > 0 ? openAssignments[0] : null;
    const assignmentEventId = openAssignment?.eventId || null;
    console.log('[PersonCard] Creating match - openAssignments count:', openAssignments.length, 'assignmentEventId:', assignmentEventId);

    // Get cached location or reuse in-flight promise
    let locationPromise = getLocationPromise();
    if (!locationPromise) {
      locationPromise = getCurrentLocation(settings).then(loc => {
        updateCachedLocation(loc);
        setLocationPromise(null);
        return loc;
      });
      setLocationPromise(locationPromise);
    }

    // Get location first (fast if cached/in-flight)
    const location = await locationPromise;

    // Add optimistic match event immediately
    addPendingMatchEvent({
      personName,
      personImage: person.image,
      person,
      openAssignment,
      location,
      matchSource,
      matchAccuracy,
      capturedImageUrl: capturedImage
    });

    // Open drawer immediately (non-blocking UX!)
    openHistoryDrawer();

    // Create real match event in background
    try {
      await createMatchEvent({
        personName,
        personImage: person.image,
        person,
        assignmentEventId,
        location,
        matchSource,
        matchAccuracy,
        capturedImageUrl: capturedImage
      });
    } catch (error) {
      // Event creation failed but optimistic event still shows
    }
  };

  const handleCardClick = (e) => {
    if (canEdit) {
      // Edit mode: Open characteristics editor
      e.preventDefault();
      setEditorOpen(true);
    }
    // Non-edit: Let the CardActionArea handle the external link
  };

  return (
    <Box>
      {/* Characteristics Editor Dialog (Edit Mode) */}
      {canEdit && (
        <CharacteristicsEditor
          person={person}
          open={editorOpen}
          onClose={() => setEditorOpen(false)}
        />
      )}

      <Card
        sx={{
          width: 150,
          position: 'relative',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4,
          }
        }}
      >
        {/* Operator Mode: Show add match button */}
        {!canEdit && (
          <Tooltip title="Add match event">
            <IconButton
              sx={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                zIndex: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(4px)',
                boxShadow: 2,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 1)',
                  transform: 'scale(1.1)',
                },
              }}
              size="small"
              color="primary"
              onClick={handleMatchClick}
            >
              <GroupAddIcon />
            </IconButton>
          </Tooltip>
        )}

        <CardActionArea
          href={canEdit ? undefined : person.uri}
          target={canEdit ? undefined : "_blank"}
          rel={canEdit ? undefined : "noreferrer"}
          disabled={!active}
          onClick={handleCardClick}
        >
          <CardMedia
            component="img"
            height={150}
            image={person.image}
            alt={`${person.first_name} ${person.last_name}`}
            sx={{
              objectFit: 'cover',
              backgroundColor: 'grey.100'
            }}
          />
        </CardActionArea>

        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack spacing={1} alignItems="center">
            <Typography
              variant="body2"
              align="center"
              sx={{
                fontWeight: 500,
                lineHeight: 1.2,
                fontSize: '0.75rem',
                minHeight: 28
              }}
            >
              {person.first_name || ''} {person.last_name || ''}
            </Typography>

            <Stack direction="row" spacing={0.5} justifyContent="center">
              {person.persontype && (
                <Tooltip title={person.persontype}>
                  <Box
                    component="img"
                    src={getCharacteristicsImagePath("PersonType", person.persontype)}
                    alt={person.persontype}
                    sx={{
                      width: 20,
                      height: 20,
                      filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))'
                    }}
                  />
                </Tooltip>
              )}
              {person.party && (
                <Tooltip title={person.party}>
                  <Box
                    component="img"
                    src={getCharacteristicsImagePath("Party", person.party)}
                    alt={person.party}
                    sx={{
                      width: 20,
                      height: 20,
                      filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.1))'
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

// Memoize to prevent re-renders when parent updates but props haven't changed
// This is especially important in search results where many cards are rendered
export default React.memo(PersonCard, (prevProps, nextProps) => {
  // Only re-render if person ID or active state changes
  return prevProps.person.person_id === nextProps.person.person_id &&
         prevProps.active === nextProps.active &&
         prevProps.matchSource === nextProps.matchSource;
});
